import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import localforage from 'localforage';

const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch';

const MaintenanceController = ({ token, onStatusChange }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 768;
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState(30);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [affectedUsers, setAffectedUsers] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedSection, setExpandedSection] = useState('');
  const [authToken, setAuthToken] = useState(token);

  // ✅ NEW: Admin Ranking State
  const [isResettingRankings, setIsResettingRankings] = useState(false);
  const [adminLeaderboard, setAdminLeaderboard] = useState([]);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    
    // Get token from props or localforage if not provided
    const getToken = async () => {
      if (token) {
        setAuthToken(token);
        fetchMaintenanceStatus(token);
        fetchAdminLeaderboard(token);
      } else {
        const storedToken = await localforage.getItem('token');
        if (storedToken) {
          setAuthToken(storedToken);
          fetchMaintenanceStatus(storedToken);
          fetchAdminLeaderboard(storedToken);
        }
      }
    };
    
    getToken();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [token]);
  
  // Fetch current maintenance status
  const fetchMaintenanceStatus = async (authTokenParam) => {
    const currentToken = authTokenParam || authToken;
    if (!currentToken) return;
    
    try {
      setIsRefreshing(true);
      const response = await axios.get(`${BASE_URL}/api/maintenance/status`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      
      if (response.data) {
        setIsMaintenanceActive(response.data.inMaintenance || false);
        setMaintenanceMessage(response.data.message || '');
        setEstimatedDuration(response.data.estimatedDurationMinutes || 30);
        setLastUpdated(response.data.lastUpdated ? new Date(response.data.lastUpdated) : new Date());
        
        if (response.data.affectedUsers) {
          setAffectedUsers(response.data.affectedUsers);
        }

        // Notify parent component if provided
        if (onStatusChange) {
          onStatusChange(response.data.inMaintenance);
        }
      }
      setErrorMessage('');
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
      setErrorMessage('Failed to fetch maintenance status. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Toggle maintenance mode
  const toggleMaintenanceMode = async () => {
    if (!authToken) return;
    
    try {
      setIsLoading(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      const endpoint = isMaintenanceActive ? 
        `${BASE_URL}/api/maintenance/disable` : 
        `${BASE_URL}/api/maintenance/enable`;
      
      const payload = !isMaintenanceActive ? {
        message: maintenanceMessage,
        estimatedDurationMinutes: parseInt(estimatedDuration)
      } : {};
      
      const response = await axios.post(
        endpoint,
        payload,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );
      
      if (response.status === 200) {
        setSuccessMessage(`Maintenance mode ${isMaintenanceActive ? 'disabled' : 'enabled'} successfully`);
        setIsMaintenanceActive(!isMaintenanceActive);
        setLastUpdated(new Date());
        
        // Notify parent component if provided
        if (onStatusChange) {
          onStatusChange(!isMaintenanceActive);
        }
      }
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      setErrorMessage('Failed to update maintenance status. Please check your permissions and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Update maintenance settings without toggling
  const updateMaintenanceSettings = async () => {
    if (!authToken) return;
    
    if (!isMaintenanceActive) {
      setSuccessMessage('Settings saved. Enable maintenance mode to apply them.');
      return;
    }
    
    try {
      setIsLoading(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      const response = await axios.post(
        `${BASE_URL}/api/maintenance/enable`,
        {
          message: maintenanceMessage,
          estimatedDurationMinutes: parseInt(estimatedDuration),
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );
      
      if (response.status === 200) {
        setSuccessMessage('Maintenance settings updated successfully');
        setLastUpdated(new Date());
        
        // Refresh to get latest status
        fetchMaintenanceStatus();
      }
    } catch (error) {
      console.error('Error updating maintenance settings:', error);
      setErrorMessage('Failed to update maintenance settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle expanded section
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  // ✅ NEW: Fetch Admin Leaderboard
  const fetchAdminLeaderboard = async (authTokenParam) => {
    const currentToken = authTokenParam || authToken;
    if (!currentToken) return;
    
    try {
      setIsLoadingLeaderboard(true);
      const response = await axios.get(`${BASE_URL}/api/admin/leaderboard?campId=1`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      
      if (response.data && response.data.leaderboard) {
        setAdminLeaderboard(response.data.leaderboard);
      }
    } catch (error) {
      console.error('Error fetching admin leaderboard:', error);
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  // ✅ NEW: Reset All Admin Rankings
  const resetAllAdminRankings = async () => {
    if (!authToken) return;
    
    if (!window.confirm('⚠️ Möchtest du wirklich ALLE Admin-Rankings zurücksetzen? Diese Aktion kann nicht rückgängig gemacht werden!')) {
      return;
    }
    
    try {
      setIsResettingRankings(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      const response = await axios.post(
        `${BASE_URL}/api/admin/leaderboard/reset`,
        {},
        {
          headers: { 
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.success) {
        setSuccessMessage(`✅ Erfolgreich ${response.data.resetCount} Admin-Rankings zurückgesetzt!`);
        fetchAdminLeaderboard();
      }
    } catch (error) {
      console.error('Error resetting admin rankings:', error);
      setErrorMessage('Fehler beim Zurücksetzen der Admin-Rankings. Bitte überprüfe deine Berechtigungen.');
    } finally {
      setIsResettingRankings(false);
    }
  };

  // ✅ NEW: Reset Single Admin Ranking
  const resetSingleAdminRanking = async (adminId, adminName) => {
    if (!authToken) return;
    
    if (!window.confirm(`Möchtest du das Ranking von ${adminName} zurücksetzen?`)) {
      return;
    }
    
    try {
      setIsResettingRankings(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      const response = await axios.post(
        `${BASE_URL}/api/admin/leaderboard/reset`,
        {
          adminId: adminId,
          campId: 1
        },
        {
          headers: { 
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data && response.data.success) {
        setSuccessMessage(`✅ Ranking von ${adminName} erfolgreich zurückgesetzt!`);
        fetchAdminLeaderboard();
      }
    } catch (error) {
      console.error('Error resetting single admin ranking:', error);
      setErrorMessage('Fehler beim Zurücksetzen des Admin-Rankings.');
    } finally {
      setIsResettingRankings(false);
    }
  };

  // ✅ NEW: Get Rank Color
  const getRankColor = (rank) => {
    const colors = {
      'Beginner': '#64748B',
      'Helper': '#059669',
      'Guardian': '#0D9488',
      'Coordinator': '#0284C7',
      'Supervisor': '#7C3AED',
      'Master': '#DC2626',
      'Legend': '#B45309'
    };
    return colors[rank] || '#64748B';
  };

  // Calculate remaining time
  const calculateRemainingTime = () => {
    if (!lastUpdated || !estimatedDuration) return 'Unknown';
    
    const endTime = new Date(lastUpdated.getTime() + (estimatedDuration * 60 * 1000));
    const now = new Date();
    
    if (endTime > now) {
      const diffMinutes = Math.floor((endTime - now) / (1000 * 60));
      
      if (diffMinutes >= 60) {
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        return `${hours}h ${minutes}m`;
      }
      
      return `${diffMinutes} minutes`;
    }
    
    return 'Expired';
  };

  const styles = {
    container: {
      padding: '32px',
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      marginBottom: '24px',
      maxWidth: isMobile ? '100%' : '800px',
      margin: '0 auto',
      width: isMobile ? "93%" : 'auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: '1px solid #e5e7eb'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1a2035',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    badge: {
      padding: '4px 12px', 
      backgroundColor: '#ef4444', 
      color: 'white',
      borderRadius: '50px',
      fontSize: '12px',
      fontWeight: 'bold'
    },
    description: {
      fontSize: '14px',
      color: '#6e7a90',
      marginBottom: '24px',
      lineHeight: '1.6'
    },
    statusCard: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      borderRadius: '10px',
      marginBottom: '24px',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)'
    },
    activeStatusCard: {
      backgroundColor: '#fee2e2',
      borderLeft: '4px solid #ef4444'
    },
    inactiveStatusCard: {
      backgroundColor: '#dcfce7',
      borderLeft: '4px solid #10b981'
    },
    statusInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    statusIcon: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px'
    },
    activeIcon: {
      backgroundColor: '#fecaca',
      color: '#ef4444'
    },
    inactiveIcon: {
      backgroundColor: '#bbf7d0',
      color: '#10b981'
    },
    statusTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a2035',
      marginBottom: '4px'
    },
    statusTime: {
      fontSize: '13px',
      color: '#6e7a90',
      margin: 0
    },
    toggleButton: {
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    enableButton: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#ef4444'
    },
    disableButton: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10b981'
    },
    accordionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      marginBottom: '12px',
      cursor: 'pointer',
      border: '1px solid #e5e7eb'
    },
    accordionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a2035',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    accordionContent: {
      padding: '20px',
      backgroundColor: '#ffffff',
      borderRadius: '0 0 8px 8px',
      marginTop: '-1px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      borderTop: 'none'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#1a2035',
      marginBottom: '8px'
    },
    textarea: {
      width: '100%',
      padding: '12px 16px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      fontSize: '14px',
      minHeight: '100px',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    inputGroup: {
      display: 'flex',
      alignItems: 'center'
    },
    inputWithIcon: {
      flex: 1,
      padding: '10px 16px',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      fontSize: '14px'
    },
    iconPrefix: {
      marginRight: '10px',
      color: '#6e7a90',
      fontSize: '18px'
    },
    helperText: {
      fontSize: '12px',
      color: '#6e7a90',
      marginTop: '4px'
    },
    statsGrid: {
      display: 'flex',
      gap: '24px',
      marginBottom: '16px',
      flexDirection: isMobile ? 'column' : 'row'
    },
    statItem: {
      flex: 1
    },
    statLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1a2035',
      margin: '0 0 4px 0'
    },
    statValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#f97316',
      margin: 0
    },
    refreshButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      backgroundColor: 'transparent',
      border: '1px solid #f97316',
      borderRadius: '8px',
      color: '#f97316',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer'
    },
    actionButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '16px',
      marginTop: '24px',
      flexDirection: isMobile ? 'column' : 'row'
    },
    secondaryButton: {
      padding: '12px 24px',
      backgroundColor: 'transparent',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      color: '#6e7a90',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      textAlign: 'center'
    },
    primaryButton: {
      padding: '12px 24px',
      backgroundColor: '#da291c',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      position: 'relative',
      textAlign: 'center'
    },
    loadingSpinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '50%',
      borderTopColor: '#ffffff',
      animation: 'spin 1s linear infinite',
      marginLeft: '8px'
    },
    successMessage: {
      marginTop: '16px',
      padding: '12px 16px',
      backgroundColor: '#dcfce7',
      borderRadius: '8px',
      color: '#10b981',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    errorMessage: {
      marginTop: '16px',
      padding: '12px 16px',
      backgroundColor: '#fee2e2',
      borderRadius: '8px',
      color: '#ef4444',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    // ✅ NEW: Admin Ranking Styles
    leaderboardList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginBottom: '16px',
      maxHeight: '400px',
      overflowY: 'auto'
    },
    adminCard: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      transition: 'all 0.2s'
    },
    adminInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flex: 1
    },
    adminRank: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#6e7a90',
      minWidth: '40px',
      textAlign: 'center'
    },
    adminAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      fontWeight: '600',
      color: '#ffffff'
    },
    adminDetails: {
      flex: 1
    },
    adminName: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#1a2035',
      marginBottom: '4px'
    },
    adminRankBadge: {
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      color: '#ffffff'
    },
    adminStats: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '12px' : '24px',
      marginRight: isMobile ? '8px' : '16px'
    },
    statBox: {
      textAlign: 'center'
    },
    statNumber: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: '#1a2035',
      marginBottom: '2px'
    },
    statLabel: {
      fontSize: '11px',
      color: '#6e7a90',
      textTransform: 'uppercase'
    },
    resetSingleButton: {
      padding: '8px 12px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: 'none',
      borderRadius: '6px',
      color: '#ef4444',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    resetAllButton: {
      padding: '12px 24px',
      backgroundColor: '#ef4444',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      marginTop: '16px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#6e7a90',
      fontSize: '14px'
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      <div style={styles.header}>
        <h2 style={styles.title}>
          <span style={{ fontSize: '24px' }}>⚠️</span>
          Maintenance Mode
        </h2>
        
        {isMaintenanceActive && (
          <div style={styles.badge}>
            ACTIVE
          </div>
        )}
      </div>
      
      <p style={styles.description}>
        When maintenance mode is enabled, regular users will see a maintenance page instead of 
        accessing the app. Only administrators can still log in and use the app normally.
      </p>
      
      {/* Status Card */}
      <div style={{ 
        ...styles.statusCard, 
        ...(isMaintenanceActive ? styles.activeStatusCard : styles.inactiveStatusCard) 
      }}>
        <div style={styles.statusInfo}>
          <div style={{ 
            ...styles.statusIcon, 
            ...(isMaintenanceActive ? styles.activeIcon : styles.inactiveIcon) 
          }}>
            {isMaintenanceActive ? '✕' : '✓'}
          </div>
          
          <div>
            <h3 style={styles.statusTitle}>
              Maintenance Mode is <span style={{ 
                color: isMaintenanceActive ? '#ef4444' : '#10b981' 
              }}>{isMaintenanceActive ? 'ACTIVE' : 'INACTIVE'}</span>
            </h3>
            {lastUpdated && (
              <p style={styles.statusTime}>
                Last updated: {lastUpdated.toLocaleString()}
              </p>
            )}
          </div>
        </div>
        
        {/* Toggle Button */}
        <motion.button 
          style={{ 
            ...styles.toggleButton, 
            ...(isMaintenanceActive ? styles.disableButton : styles.enableButton) 
          }} 
          onClick={toggleMaintenanceMode}
          disabled={isLoading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isMaintenanceActive ? '🔓 Turn Off' : '🔒 Turn On'}
        </motion.button>
      </div>
      
      {/* Configuration Section */}
      <motion.div 
        style={styles.accordionHeader} 
        onClick={() => toggleSection('config')}
        whileHover={{ backgroundColor: '#f1f5f9' }}
      >
        <h3 style={styles.accordionTitle}>
          <span>⚙️</span> Configuration
        </h3>
        <span style={{ 
          transform: expandedSection === 'config' ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.3s'
        }}>▼</span>
      </motion.div>
      
      <AnimatePresence>
        {expandedSection === 'config' && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={styles.accordionContent}
          >
            <div style={styles.formGroup}>
              <label style={styles.label}>Maintenance Message</label>
              <textarea
                value={maintenanceMessage}
                onChange={(e) => setMaintenanceMessage(e.target.value)}
                placeholder="We're currently performing scheduled maintenance to improve your experience. Please check back shortly."
                style={styles.textarea}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Estimated Duration (minutes)</label>
              <div style={styles.inputGroup}>
                <span style={styles.iconPrefix}>⏱️</span>
                <input
                  type="number"
                  value={estimatedDuration}
                  onChange={(e) => setEstimatedDuration(e.target.value)}
                  min="5"
                  max="1440"
                  style={styles.inputWithIcon}
                />
              </div>
              <p style={styles.helperText}>
                Users will see a countdown based on this value
              </p>
            </div>
            
            <motion.button 
              style={styles.primaryButton}
              onClick={updateMaintenanceSettings}
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Save Settings
              {isLoading && (
                <span style={styles.loadingSpinner} />
              )}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* ✅ NEW: Admin Rankings Section */}
      <motion.div 
        style={styles.accordionHeader} 
        onClick={() => toggleSection('rankings')}
        whileHover={{ backgroundColor: '#f1f5f9' }}
      >
        <h3 style={styles.accordionTitle}>
          <span>🏆</span> Admin Rankings
        </h3>
        <span style={{ 
          transform: expandedSection === 'rankings' ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.3s'
        }}>▼</span>
      </motion.div>
      
      <AnimatePresence>
        {expandedSection === 'rankings' && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={styles.accordionContent}
          >
            <div style={styles.formGroup}>
              <label style={styles.label}>Admin Leaderboard</label>
              <p style={styles.helperText}>
                Verwalte Admin-Checkout-Rankings. Rankings können für einzelne Admins oder alle auf einmal zurückgesetzt werden.
              </p>
            </div>

            {isLoadingLeaderboard ? (
              <div style={styles.emptyState}>
                <span style={styles.loadingSpinner} />
                <p>Lade Leaderboard...</p>
              </div>
            ) : adminLeaderboard.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={{ fontSize: '32px', marginBottom: '8px' }}>🏆</p>
                <p>Keine Admin-Rankings vorhanden</p>
              </div>
            ) : (
              <>
                <div style={styles.leaderboardList}>
                  {adminLeaderboard.map((admin, index) => (
                    <div key={admin.userId} style={styles.adminCard}>
                      <div style={styles.adminInfo}>
                        <div style={styles.adminRank}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                        </div>
                        
                        <div 
                          style={{
                            ...styles.adminAvatar,
                            backgroundColor: getRankColor(typeof admin.rank === 'object' ? admin.rank?.name : admin.rank)
                          }}
                        >
                          {admin.username?.charAt(0).toUpperCase() || '?'}
                        </div>
                        
                        <div style={styles.adminDetails}>
                          <div style={styles.adminName}>{admin.username || 'Unknown Admin'}</div>
                          <span 
                            style={{
                              ...styles.adminRankBadge,
                              backgroundColor: getRankColor(typeof admin.rank === 'object' ? admin.rank?.name : admin.rank)
                            }}
                          >
                            {typeof admin.rank === 'object' ? admin.rank?.name : admin.rank}
                          </span>
                        </div>
                      </div>

                      <div style={styles.adminStats}>
                        <div style={styles.statBox}>
                          <div style={styles.statNumber}>{admin.totalCheckouts || 0}</div>
                          <div style={styles.statLabel}>Checkouts</div>
                        </div>
                      </div>

                      <motion.button
                        style={styles.resetSingleButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          resetSingleAdminRanking(admin.userId, admin.username);
                        }}
                        disabled={isResettingRankings}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        🔄 Reset
                      </motion.button>
                    </div>
                  ))}
                </div>

                <motion.button
                  style={styles.resetAllButton}
                  onClick={resetAllAdminRankings}
                  disabled={isResettingRankings}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isResettingRankings ? (
                    <>
                      <span style={styles.loadingSpinner} />
                      Wird zurückgesetzt...
                    </>
                  ) : (
                    <>
                      🔄 Alle Rankings zurücksetzen
                    </>
                  )}
                </motion.button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Status Information Section */}
      {isMaintenanceActive && (
        <>
          <motion.div 
            style={styles.accordionHeader} 
            onClick={() => toggleSection('status')}
            whileHover={{ backgroundColor: '#f1f5f9' }}
          >
            <h3 style={styles.accordionTitle}>
              <span>📊</span> Current Status
            </h3>
            <span style={{ 
              transform: expandedSection === 'status' ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.3s'
            }}>▼</span>
          </motion.div>
          
          <AnimatePresence>
            {expandedSection === 'status' && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={styles.accordionContent}
              >
                <div style={styles.statsGrid}>
                  <div style={styles.statItem}>
                    <p style={styles.statLabel}>Affected Users</p>
                    <p style={styles.statValue}>{affectedUsers.length || 'All'}</p>
                  </div>
                  
                  <div style={styles.statItem}>
                    <p style={styles.statLabel}>Time Remaining</p>
                    <p style={styles.statValue}>{calculateRemainingTime()}</p>
                  </div>
                  
                  <div style={styles.statItem}>
                    <p style={styles.statLabel}>Started</p>
                    <p style={styles.statValue}>
                      {lastUpdated ? 
                        lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                        'Unknown'
                      }
                    </p>
                  </div>
                </div>
                
                <motion.button 
                  style={styles.refreshButton} 
                  onClick={() => fetchMaintenanceStatus()}
                  disabled={isRefreshing}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isRefreshing ? '🔄 Refreshing...' : '🔄 Refresh Status'}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
      
      {/* Action Buttons */}
      <div style={styles.actionButtons}>
        <motion.button
          style={styles.secondaryButton}
          onClick={() => fetchMaintenanceStatus()}
          disabled={isRefreshing}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </motion.button>
        
        <motion.button
          style={styles.primaryButton}
          onClick={toggleMaintenanceMode}
          disabled={isLoading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isMaintenanceActive ? 'Disable Maintenance Mode' : 'Enable Maintenance Mode'}
          {isLoading && (
            <span style={styles.loadingSpinner} />
          )}
        </motion.button>
      </div>
      
      {/* Messages */}
      {successMessage && (
        <div style={styles.successMessage}>
          <span>✅</span> {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div style={styles.errorMessage}>
          <span>⚠️</span> {errorMessage}
        </div>
      )}
    </div>
  );
};

export default MaintenanceController; 